import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Minus, Plus } from 'lucide-react'
import * as Sentry from '@sentry/react'
import { Capacitor } from '@capacitor/core'
import { inferUnitFromName, defaultUnitForCategory } from '../../lib/itemDefaults'
import { checkLimit, incrementUsage, FREE_LIMITS } from '../../lib/usageLimits'
import { track } from '../../lib/posthog'
import { debugLog, debugLogError, startDebugSession } from '../../lib/debugLogger'

// We use navigator.mediaDevices.getUserMedia on every platform — web,
// iOS PWA, and the Capacitor Android wrap. The Capacitor Camera plugin
// was previously used on Android, but launching the OS camera Activity
// caused MainActivity to be paused/killed under memory pressure during
// the long Claude API call, losing all React state when the user
// returned. Staying inside the WebView keeps the React tree mounted
// for the entire capture → API → review flow.
//
// IS_NATIVE is still used below for API_BASE (relative-URL routing
// breaks inside the Capacitor WebView's https://localhost origin).
const IS_NATIVE = Capacitor.isNativePlatform()

// Inside the Capacitor WebView, relative URLs like '/api/claude-receipt'
// resolve to https://localhost (the WebView's origin) — nothing serves
// /api there, so the request returns the SPA fallback HTML and JSON.parse
// blows up silently. From native we MUST hit the Vercel functions on the
// production origin directly. Web/PWA keeps the relative URL so it works
// across all environments (production, preview, localhost dev).
const API_BASE = IS_NATIVE ? 'https://www.pantryioapp.com' : ''

export interface ReceiptItem {
  name:           string
  raw?:           string
  quantity:       number
  unit_price?:    number | null
  total_price?:   number | null
  category:       string
  tracks_expiry?: boolean
  confidence?:    'high' | 'medium' | 'low'
}

interface ReceiptResult {
  store?:           string
  store_location?:  string
  date?:            string
  subtotal?:        number | null
  total?:           number | null
  tax?:             number | null
  items:            ReceiptItem[]
}

export type ConfirmedReceiptItem = {
  name:           string
  quantity:       number
  quantity_unit:  string  // 'pint' / 'gallon' / 'can' / 'count' / etc.
  price:          number | null
  category:       string
  tracks_expiry:  boolean
  expiry_date:    string | null
  raw:            string | null
}

type EditableReceiptItem = ReceiptItem & {
  confirmed:    boolean
  expiry_date:  string | null
}

interface Props {
  onComplete:  (
    items: ConfirmedReceiptItem[],
    meta?: { store?: string; total?: number | null },
  ) => void
  onSkip:      () => void
  isPremium?:  boolean
  userId?:     string
}

export default function ReceiptScanner({ onComplete, onSkip, isPremium = false, userId }: Props) {
  const navigate  = useNavigate()
  const videoRef  = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  // One-shot guard for the "results panel rendered" debug log so the
  // render-time snapshot doesn't spam debug_logs on every re-render.
  const resultsRenderedRef = useRef(false)

  const [captured,         setCaptured]         = useState<string | null>(null)
  const [capturing,        setCapturing]        = useState(false)
  const [scanning,         setScanning]         = useState(false)
  const [results,          setResults]          = useState<ReceiptResult | null>(null)
  const [editableItems,    setEditableItems]    = useState<EditableReceiptItem[]>([])
  const [error,            setError]            = useState<string | null>(null)
  const [scanCount,        setScanCount]        = useState<number>(0)
  const [scanLimit,        setScanLimit]        = useState(false)
  // Distinguish "user denied permission" from generic camera failure so we
  // can show actionable Settings instructions instead of a vague retry button.
  const [permissionDenied, setPermissionDenied] = useState(false)

  // Start a fresh debug-log session per scanner mount so every event
  // (camera init, capture, API call, results, handoff) is grouped under
  // the same session_id in the /debug-logs viewer.
  useEffect(() => {
    startDebugSession('receipt-scan')
    void debugLog({
      feature: 'receipt-scan',
      step:    '1-scanner-mounted',
      payload: { platform: Capacitor.getPlatform(), isNative: IS_NATIVE },
    })
  }, [])

  // Seed editableItems whenever results change (e.g. after a fresh scan).
  useEffect(() => {
    if (!results?.items) { setEditableItems([]); return }
    console.log('[RS-DEBUG] results changed, seeding editableItems with', results.items.length, 'rows')
    void debugLog({
      feature: 'receipt-scan',
      step:    '9-results-effect',
      payload: { itemsCount: results.items.length },
    })
    setEditableItems(results.items.map(i => ({
      ...i,
      confirmed:   true,
      expiry_date: null,
    })))
  }, [results])

  // Net for promise rejections that escape every other catch — e.g. an
  // exception inside a setState callback during a Capacitor render. Without
  // this, those vanish into the WebView's default unhandled handler with
  // no Sentry signal. Only attached while ReceiptScanner is mounted.
  useEffect(() => {
    function onRejection(ev: PromiseRejectionEvent) {
      const reason: any = ev.reason
      console.error('[RS-DEBUG] unhandledrejection while scanner mounted:', reason)
      void debugLogError('receipt-scan', 'unhandled-rejection', reason ?? new Error('unhandledrejection (no reason)'))
      Sentry.captureException(reason ?? new Error('unhandledrejection (no reason)'), {
        tags:  { feature: 'receipt-scanner-unhandled' },
        extra: {
          platform: Capacitor.getPlatform(),
          message:  reason?.message,
          stack:    reason?.stack,
        },
      })
    }
    function onError(ev: ErrorEvent) {
      console.error('[RS-DEBUG] window.onerror while scanner mounted:', ev.message, ev.error)
      void debugLogError('receipt-scan', 'window-error', ev.error ?? new Error(ev.message))
      Sentry.captureException(ev.error ?? new Error(ev.message), {
        tags:  { feature: 'receipt-scanner-windowerror' },
        extra: { platform: Capacitor.getPlatform(), filename: ev.filename, lineno: ev.lineno },
      })
    }
    window.addEventListener('unhandledrejection', onRejection)
    window.addEventListener('error', onError)
    return () => {
      window.removeEventListener('unhandledrejection', onRejection)
      window.removeEventListener('error', onError)
    }
  }, [])

  function toggleItem(idx: number) {
    setEditableItems(prev =>
      prev.map((it, i) => (i === idx ? { ...it, confirmed: !it.confirmed } : it))
    )
  }

  function updateItem(idx: number, patch: Partial<EditableReceiptItem>) {
    setEditableItems(prev =>
      prev.map((it, i) => (i === idx ? { ...it, ...patch } : it))
    )
  }

  function removeItem(idx: number) {
    setEditableItems(prev => prev.filter((_, i) => i !== idx))
  }

  function handleConfirmAll() {
    const final: ConfirmedReceiptItem[] = editableItems
      .filter(i => i.confirmed && i.name.trim().length > 0)
      .map(i => {
        const trimmed = i.name.trim()
        return {
          name:          trimmed,
          quantity:      i.quantity,
          quantity_unit: inferUnitFromName(trimmed) ?? defaultUnitForCategory(i.category),
          price:         i.total_price ?? i.unit_price ?? null,
          category:      i.category,
          tracks_expiry: i.tracks_expiry ?? false,
          expiry_date:   i.expiry_date ?? null,
          raw:           i.raw ?? null,
        }
      })
    console.log('[RS-DEBUG] 8. handleConfirmAll: confirmed=', final.length, 'of', editableItems.length)
    void debugLog({
      feature: 'receipt-scan',
      step:    '11-confirm-tapped',
      payload: { confirmed: final.length, total: editableItems.length },
    })
    if (final.length === 0) return
    try {
      track('receipt_scanned', { items_added: final.length })
      console.log('[RS-DEBUG] 9. Calling onComplete with', final.length, 'items')
      void debugLog({ feature: 'receipt-scan', step: '12-on-complete-call' })
      onComplete(final, { store: results?.store, total: results?.total ?? null })
      console.log('[RS-DEBUG] 10. onComplete returned')
      void debugLog({ feature: 'receipt-scan', step: '13-on-complete-returned' })
    } catch (err: any) {
      console.error('[RS-DEBUG] CAUGHT in handleConfirmAll:', err)
      console.error('[RS-DEBUG] Error message:', err?.message)
      console.error('[RS-DEBUG] Error stack:', err?.stack)
      void debugLogError('receipt-scan', 'handleConfirmAll', err)
      Sentry.captureException(err, {
        tags:  { feature: 'receipt-scanner-handoff' },
        extra: { platform: Capacitor.getPlatform(), itemCount: final.length },
      })
      setError(`Could not load items: ${err?.message || 'Unknown error'}. Please try again.`)
    }
  }

  // ── Check scan count on mount ──────────────────────────────────────────────

  useEffect(() => {
    if (isPremium || !userId) return
    ;(async () => {
      const result = await checkLimit(userId ?? null, isPremium, 'receipt_scans')
      if (!result) return
      setScanCount(result.used)
      if (result.hitLimit) setScanLimit(true)
    })()
  }, [isPremium, userId])

  // ── Increment scan count ───────────────────────────────────────────────────

  const incrementScanCount = useCallback(async () => {
    await incrementUsage(userId ?? null, isPremium, 'receipt_scans')
    setScanCount(c => c + 1)
  }, [userId, isPremium])

  // ── Send to Claude ──────────────────────────────────────────────────────────
  // Defined ahead of the camera-init effects so both the web capture path and
  // the native Camera plugin path can call it without forward-ref gymnastics.

  const analyzeReceipt = useCallback(async (base64: string) => {
    setScanning(true)
    setError(null)
    console.log('[RS-DEBUG] 3. Sending to /api/claude-receipt', {
      base64Length: base64.length,
      apiBase:      API_BASE || '(relative)',
      isNative:     IS_NATIVE,
    })
    void debugLog({
      feature: 'receipt-scan',
      step:    '5-api-call-start',
      payload: { base64Length: base64.length, apiBase: API_BASE || '(relative)', isNative: IS_NATIVE },
    })
    Sentry.addBreadcrumb({
      category: 'receipt-scanner',
      message:  'POST /api/claude-receipt',
      level:    'info',
      data:     { base64Length: base64.length, apiBase: API_BASE || '(relative)', isNative: IS_NATIVE },
    })
    try {
      const response = await fetch(`${API_BASE}/api/claude-receipt`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ imageBase64: base64, mediaType: 'image/jpeg' }),
      })

      console.log('[RS-DEBUG] 4. Response received, status:', response.status, 'ok:', response.ok)
      void debugLog({
        feature: 'receipt-scan',
        step:    '6-api-response',
        payload: {
          status:       response.status,
          ok:           response.ok,
          contentType:  response.headers.get('content-type'),
        },
      })

      // Defensive: in Capacitor, a misrouted relative URL falls back to the
      // SPA's index.html (HTTP 200 with HTML body). response.json() then
      // throws a SyntaxError that hides the real failure. Surface a clear
      // error instead.
      const contentType = response.headers.get('content-type') ?? ''
      if (!contentType.includes('application/json')) {
        const preview = (await response.text()).slice(0, 200)
        void debugLog({
          feature: 'receipt-scan',
          step:    '6a-bad-content-type',
          payload: { status: response.status, contentType, preview },
        })
        throw new Error(
          `Unexpected response (${response.status}, ${contentType || 'no content-type'}). Body starts with: ${preview}`
        )
      }

      const data = await response.json()
      console.log('[RS-DEBUG] 5. JSON parsed, keys:', Object.keys(data))
      console.log(
        '[RS-DEBUG] 6. Items array:', data.items?.length ?? 'MISSING',
        'first item:', JSON.stringify(data.items?.[0]).slice(0, 200)
      )
      void debugLog({
        feature: 'receipt-scan',
        step:    '7-json-parsed',
        payload: {
          keys:        Object.keys(data),
          itemsCount:  data.items?.length ?? null,
          firstItem:   data.items?.[0] ?? null,
          store:       data.store ?? null,
          total:       data.total ?? null,
        },
      })

      if (data.error) {
        console.warn('[RS-DEBUG] API returned error:', data.error)
        void debugLog({ feature: 'receipt-scan', step: '7a-api-error-field', message: String(data.error) })
        setError(data.error)
        setCaptured(null)
        return
      }

      // Defensive: confirm the shape we expect before handing to the results
      // panel. If items is missing/non-array, we'd silently render an empty
      // confirm screen and the user would think the scan failed.
      if (!Array.isArray(data.items)) {
        void debugLog({
          feature: 'receipt-scan',
          step:    '7b-items-not-array',
          payload: { itemsType: typeof data.items, jsonPreview: JSON.stringify(data).slice(0, 500) },
        })
        throw new Error(`Unexpected response shape — items is ${typeof data.items}, not array`)
      }

      await incrementScanCount()
      setResults(data as ReceiptResult)
      console.log('[RS-DEBUG] 7. setResults applied — results panel should render next paint')
      void debugLog({
        feature: 'receipt-scan',
        step:    '8-set-results',
        payload: { itemsCount: data.items.length, store: data.store ?? null },
      })
      Sentry.addBreadcrumb({
        category: 'receipt-scanner',
        message:  'setResults applied',
        level:    'info',
        data:     { itemCount: data.items.length, store: data.store ?? null },
      })
    } catch (err: any) {
      console.error('[RS-DEBUG] CAUGHT in analyzeReceipt:', err)
      console.error('[RS-DEBUG] Error message:', err?.message)
      console.error('[RS-DEBUG] Error stack:', err?.stack)
      void debugLogError('receipt-scan', 'analyzeReceipt', err)
      Sentry.captureException(err, {
        tags:  { feature: 'receipt-scanner' },
        extra: {
          platform:     Capacitor.getPlatform(),
          base64Length: base64.length,
          message:      err?.message,
          stack:        err?.stack,
        },
      })
      const friendly = err?.message
        ? `Could not analyze image: ${err.message}`
        : 'Could not analyze image. Please try again.'
      setError(friendly)
      setCaptured(null)
    } finally {
      setScanning(false)
    }
  }, [incrementScanCount])

  // ── Start camera ────────────────────────────────────────────────────────────
  // getUserMedia works inside Capacitor's Chrome WebView too — the
  // BridgeWebChromeClient forwards camera permission requests to the
  // OS-level CAMERA permission declared in AndroidManifest.xml. No
  // platform branch.

  useEffect(() => {
    if (scanLimit) return

    void debugLog({ feature: 'receipt-scan', step: '2-getUserMedia-call' })

    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },  // back camera preferred
          width:      { ideal: 2560 },
          height:     { ideal: 1920 },
        },
      })
      .then(stream => {
        streamRef.current = stream
        if (videoRef.current) videoRef.current.srcObject = stream
        setPermissionDenied(false)
        void debugLog({
          feature: 'receipt-scan',
          step:    '3-stream-acquired',
          payload: { trackCount: stream.getVideoTracks().length },
        })
      })
      .catch((err: any) => {
        console.error('Camera error:', err)
        const name = err?.name ?? ''
        const msg  = err?.message ?? String(err)
        void debugLogError('receipt-scan', `getUserMedia-${name || 'unknown'}`, err)
        Sentry.captureException(err, {
          tags:  { feature: 'receipt-scanner-getusermedia' },
          extra: { platform: Capacitor.getPlatform(), name, message: msg },
        })
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setPermissionDenied(true)
          setError(
            "Camera permission denied. To enable receipt scanning, go to your phone's Settings → Apps → PantryIO → Permissions → Camera."
          )
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          setError('No camera found on this device.')
        } else {
          setError(`Camera error: ${msg}`)
        }
      })

    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop())
    }
  }, [scanLimit])

  // Re-attempt acquiring the camera stream (used by the "Try again" button
  // when permission was denied or the device threw a generic error).
  const retryCamera = useCallback(async () => {
    setError(null)
    setPermissionDenied(false)
    void debugLog({ feature: 'receipt-scan', step: 'retry-camera' })
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width:      { ideal: 2560 },
          height:     { ideal: 1920 },
        },
      })
      streamRef.current?.getTracks().forEach(t => t.stop())
      streamRef.current = stream
      if (videoRef.current) videoRef.current.srcObject = stream
      void debugLog({
        feature: 'receipt-scan',
        step:    'retry-camera-success',
        payload: { trackCount: stream.getVideoTracks().length },
      })
    } catch (err: any) {
      const name = err?.name ?? ''
      const msg  = err?.message ?? String(err)
      void debugLogError('receipt-scan', `retry-camera-${name || 'unknown'}`, err)
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setPermissionDenied(true)
        setError(
          "Camera permission denied. To enable receipt scanning, go to your phone's Settings → Apps → PantryIO → Permissions → Camera."
        )
      } else {
        setError(`Camera error: ${msg}`)
      }
    }
  }, [])

  // ── Capture frame ──────────────────────────────────────────────────────────
  // 2048px long edge + 0.92 JPEG quality is the sweet spot for receipt OCR:
  // small text needs the resolution, thin strokes survive less compression,
  // and final size lands well under the API's 4MB cap.
  const handleCapture = async () => {
    if (!videoRef.current || !canvasRef.current) return
    if (capturing || scanning) return  // guard against double-tap

    void debugLog({ feature: 'receipt-scan', step: '4-capture-tap' })
    setCapturing(true)
    try {
      const video  = videoRef.current
      const canvas = canvasRef.current

      // Ask the back camera to do a focus pass before we snap. Not all browsers
      // support focusMode; failures are harmless — we still get the still frame
      // after the brief delay below.
      const stream = video.srcObject as MediaStream | null
      const track  = stream?.getVideoTracks()[0]
      if (track) {
        try {
          const settings = track.getSettings() as MediaTrackSettings & { focusMode?: string }
          if ('focusMode' in settings) {
            await track.applyConstraints({
              advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
            })
          }
        } catch {
          // not all browsers/devices support focus constraints — continue
        }
      }

      // Brief pause so autofocus can converge on the receipt (~250-400ms typical)
      await new Promise(resolve => setTimeout(resolve, 350))

      const maxSize = 2048
      let width  = video.videoWidth
      let height = video.videoHeight

      if (width > maxSize || height > maxSize) {
        if (width > height) { height = Math.round(height * maxSize / width); width = maxSize }
        else                { width  = Math.round(width * maxSize / height); height = maxSize }
      }

      canvas.width  = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.drawImage(video, 0, 0, width, height)

      const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
      const base64  = dataUrl.split(',')[1]
      void debugLog({
        feature: 'receipt-scan',
        step:    '5-frame-encoded',
        payload: { videoWidth: video.videoWidth, videoHeight: video.videoHeight, base64Length: base64.length },
      })
      setCaptured(dataUrl)
      analyzeReceipt(base64)
    } finally {
      setCapturing(false)
    }
  }

  // ── Quantity stepper helper ────────────────────────────────────────────────
  // Re-derives unit_price from the original total when needed, then keeps
  // total_price = unit_price × quantity in sync as the user adjusts.
  function updateItemQty(idx: number, nextQty: number) {
    setEditableItems(prev =>
      prev.map((it, i) => {
        if (i !== idx) return it
        const safeQty   = Math.max(1, Math.floor(nextQty))
        const oldQty    = Math.max(1, it.quantity)
        const unitPrice = it.unit_price ?? (it.total_price != null ? it.total_price / oldQty : null)
        return {
          ...it,
          quantity:    safeQty,
          unit_price:  unitPrice,
          total_price: unitPrice != null ? Math.round(unitPrice * safeQty * 100) / 100 : null,
        }
      })
    )
  }

  // ── Price editor helper ────────────────────────────────────────────────────
  // The user edits the TOTAL price; unit_price is always derived as total/qty.
  // Empty input clears both. Negative values are rejected.
  function updateItemPrice(idx: number, raw: string) {
    setEditableItems(prev =>
      prev.map((it, i) => {
        if (i !== idx) return it
        if (raw === '' || raw === '.') {
          return { ...it, total_price: null, unit_price: null }
        }
        const parsed = parseFloat(raw)
        if (Number.isNaN(parsed) || parsed < 0) return it
        const total = Math.round(parsed * 100) / 100
        const qty   = Math.max(1, it.quantity)
        return {
          ...it,
          total_price: total,
          unit_price:  total / qty,
        }
      })
    )
  }

  // ── Scan limit screen ──────────────────────────────────────────────────────

  if (scanLimit) {
    return (
      <div className="fixed inset-0 z-50 bg-[#FAF8F4] flex flex-col items-center justify-center px-6 text-center">
        <div className="text-5xl mb-4">🧾</div>
        <h2 className="text-xl font-bold text-[#2C2A26] mb-2">
          Monthly limit reached
        </h2>
        <p className="text-sm text-[#7A7468] mb-2 leading-relaxed">
          Free accounts can scan {FREE_LIMITS.receipt_scans} receipts per month.
          You've used all {FREE_LIMITS.receipt_scans} this month.
        </p>
        <p className="text-xs text-[#9A9491] mb-8">
          Resets on the 1st of next month, or upgrade for unlimited scans.
        </p>

        <div className="w-full max-w-[320px] flex flex-col gap-3">
          <button
            onClick={() => navigate('/paywall')}
            className="w-full py-4 rounded-2xl text-white font-semibold text-sm"
            style={{ background: '#6B9E78' }}
          >
            Upgrade to Pantry+ — Unlimited scans
          </button>
          <button
            onClick={onSkip}
            className="w-full py-3 text-sm text-[#9A9491]"
          >
            Maybe later
          </button>
        </div>
      </div>
    )
  }

  // ── Main scanner ───────────────────────────────────────────────────────────

  // Render-time state snapshot — proves to DevTools which branch is about to
  // render. If [RS-DEBUG] 7 fires (setResults applied) but this never logs
  // with results=true, React swallowed the update somehow. Also writes to
  // debug_logs the FIRST time results becomes truthy (so the remote
  // viewer sees a clear "results panel rendered" event without spamming).
  console.log('[RS-DEBUG] RENDER state:', {
    captured:      !!captured,
    scanning,
    error:         !!error,
    results:       !!results,
    resultsItems:  results?.items?.length ?? null,
    editableItems: editableItems.length,
  })
  if (results && !resultsRenderedRef.current) {
    resultsRenderedRef.current = true
    void debugLog({
      feature: 'receipt-scan',
      step:    '10-render-results-panel',
      payload: { itemsCount: results.items?.length ?? 0 },
    })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 bg-black/60">
        <button onClick={onSkip} className="text-white text-sm bg-white/20 px-4 py-2 rounded-full">
          Skip
        </button>
        <p className="text-white text-sm font-medium">Scan Receipt</p>
        {/* Scan count badge for free users */}
        {!isPremium && (
          <div className="bg-white/20 px-3 py-1 rounded-full">
            <p className="text-white text-xs font-medium">
              {Math.max(0, FREE_LIMITS.receipt_scans - scanCount)} left
            </p>
          </div>
        )}
        {isPremium && <div className="w-16" />}
      </div>

      {/* Camera / preview — same in-WebView <video>/<canvas> pipeline on
          every platform now (web, iOS PWA, and Capacitor Android). */}
      <div className="flex-1 relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
        />
        <canvas ref={canvasRef} className="hidden" />
        {!captured && !permissionDenied && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="border-2 border-white rounded-xl w-64 h-96 opacity-50" />
          </div>
        )}

        {captured && (
          <img src={captured} alt="Receipt" className="absolute inset-0 w-full h-full object-cover" />
        )}
      </div>

      {/* Bottom panel */}
      {!captured ? (
        <div className="px-4 pb-10 pt-4 text-center bg-black/60">
          {permissionDenied ? (
            <>
              <p className="text-white/90 text-sm mb-4 leading-relaxed max-w-[320px] mx-auto">
                {error}
              </p>
              <button
                onClick={retryCamera}
                className="text-white text-sm bg-white/20 px-6 py-2 rounded-full"
              >
                Try again
              </button>
            </>
          ) : error ? (
            <>
              <p className="text-white/90 text-sm mb-4 leading-relaxed max-w-[320px] mx-auto">
                {error}
              </p>
              <button
                onClick={retryCamera}
                className="text-white text-sm bg-white/20 px-6 py-2 rounded-full"
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <p className="text-white/70 text-sm mb-5">
                {capturing ? 'Focusing…' : 'Position receipt in the frame'}
              </p>
              <button
                onClick={handleCapture}
                disabled={capturing || scanning}
                aria-label="Capture receipt"
                className="w-16 h-16 rounded-full bg-white mx-auto flex items-center justify-center disabled:opacity-70"
              >
                {capturing ? (
                  <div className="w-8 h-8 rounded-full border-[3px] border-[#6B9E78] border-t-transparent animate-spin" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#6B9E78]" />
                )}
              </button>
            </>
          )}
        </div>

      ) : scanning ? (
        <div className="px-4 pb-10 pt-4 text-center bg-black/60">
          <div className="w-8 h-8 rounded-full border-2 border-white border-t-transparent animate-spin mx-auto mb-3" />
          <p className="text-white text-sm">Reading your receipt…</p>
        </div>

      ) : error ? (
        <div className="px-4 pb-10 pt-4 text-center bg-black/60">
          <p className="text-white text-sm mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null)
              setCaptured(null)
              // Live <video> stream is still mounted — clearing captured
              // returns the user straight to the framing view.
            }}
            className="text-white text-sm bg-white/20 px-6 py-2 rounded-full"
          >
            Try again
          </button>
        </div>

      ) : results ? (
        <div className="bg-white rounded-t-3xl p-4 max-h-[78vh] overflow-y-auto">

          {/* Summary card */}
          <div className="mb-3 rounded-[14px] border border-[#EDE7DC] bg-[#FAF8F4] p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-[#9A9491] mb-1">
                  Receipt
                </div>
                {results.store && (
                  <div className="font-medium text-[15px] text-[#2C2A26] truncate">
                    {results.store}
                    {results.store_location && (
                      <span className="font-normal text-[#7A7468]"> · {results.store_location}</span>
                    )}
                  </div>
                )}
                {results.date && (
                  <div className="text-[12px] italic text-[#9A9491] mt-0.5">
                    {results.date}
                  </div>
                )}
              </div>
              {results.total != null && (
                <div className="text-right flex-shrink-0">
                  <div className="text-[18px] font-medium text-[#2C2A26] tabular-nums">
                    ${results.total.toFixed(2)}
                  </div>
                  <div className="text-[11px] italic text-[#9A9491]">total</div>
                </div>
              )}
            </div>
            {editableItems.length > 0 && (
              <div className="mt-3 pt-2 border-t border-[#EDE7DC] flex justify-between text-[12px] italic text-[#7A7468]">
                <span>
                  {editableItems.filter(i => i.confirmed).length} of {editableItems.length} items
                </span>
                <span className="tabular-nums">
                  ${editableItems
                    .reduce((sum, i) => sum + (i.confirmed ? (i.total_price ?? 0) : 0), 0)
                    .toFixed(2)} matched
                </span>
              </div>
            )}
          </div>

          {/* Header row: rescan */}
          <div className="flex items-center justify-between mb-3 px-1">
            <p className="text-[13px] font-medium text-[#2C2A26]">
              Confirm what came home
            </p>
            <button
              onClick={() => {
                setCaptured(null)
                setResults(null)
                resultsRenderedRef.current = false
                // Live <video> stream stays mounted — clearing captured +
                // results returns the user to the framing view.
              }}
              className="text-[12px] text-[#7A7468] font-medium"
            >
              Rescan
            </button>
          </div>

          {/* Few-items warning */}
          {editableItems.length > 0 && editableItems.length < 5 && (
            <div className="mb-3 rounded-xl bg-[#FBF1E5] border border-[#E8C9A2] px-3 py-2.5">
              <p className="text-xs text-[#8A6A2C] leading-snug">
                Only {editableItems.length} item{editableItems.length === 1 ? '' : 's'} detected.
                If your receipt has more, try rescanning with better lighting and a flat angle.
              </p>
            </div>
          )}

          {/* Items */}
          <div className="rounded-[14px] border border-[#EDE7DC] bg-white overflow-hidden mb-4">
            {editableItems.map((item, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 px-3 py-3 border-b border-[#F0EDE8] last:border-b-0 ${
                  item.confidence === 'low' ? 'bg-[#FBF1E5]/40' : ''
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleItem(index)}
                  className={`mt-0.5 h-6 w-6 rounded-full flex-shrink-0 flex items-center justify-center transition ${
                    item.confirmed
                      ? 'bg-[#6B9E78] text-white'
                      : 'border-[1.5px] border-[#D4C4A8] bg-white'
                  }`}
                  aria-label={item.confirmed ? 'Unconfirm' : 'Confirm'}
                >
                  {item.confirmed && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <input
                    value={item.name}
                    onChange={e => updateItem(index, { name: e.target.value })}
                    className={`w-full text-[14px] font-medium bg-transparent border-none focus:outline-none focus:bg-[#FAF8F4] rounded px-1 py-0.5 -ml-1 ${
                      item.confirmed ? 'text-[#2C2A26]' : 'text-[#9A9491] line-through'
                    }`}
                  />

                  {item.raw && item.raw.trim() && item.raw.trim().toLowerCase() !== item.name.trim().toLowerCase() && (
                    <p className="text-[10px] uppercase tracking-[0.08em] text-[#9A9491] mt-1 px-1 truncate">
                      Receipt: {item.raw}
                    </p>
                  )}

                  {/* Price editor + low-confidence badge */}
                  <div className="mt-1 flex items-center gap-2 flex-wrap px-1">
                    <div className="flex items-center gap-1">
                      <span className="text-[12px] italic text-[#7A7468]">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        value={item.total_price ?? ''}
                        onChange={e => updateItemPrice(index, e.target.value)}
                        onFocus={e => e.target.select()}
                        aria-label="Total price"
                        className="w-[70px] rounded-[8px] border border-[#EDE7DC] bg-[#FAF8F4] px-2 py-1 text-[12px] text-[#2C2A26] tabular-nums focus:outline-none focus:border-[#6B9E78]"
                      />
                      {item.quantity > 1 && item.total_price != null && (
                        <span className="text-[11px] italic text-[#9A9491] tabular-nums">
                          (${(item.total_price / Math.max(1, item.quantity)).toFixed(2)} each)
                        </span>
                      )}
                    </div>
                    {item.confidence === 'low' && (
                      <span className="rounded-full bg-[#F8E0CC] text-[#9D5A2C] text-[9px] uppercase tracking-[0.1em] font-medium px-2 py-0.5">
                        Verify
                      </span>
                    )}
                  </div>

                  {/* Quantity stepper */}
                  <div className="mt-2 flex items-center gap-3 px-1">
                    <span className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#9A9491]">
                      Quantity
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateItemQty(index, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                        aria-label="Decrease quantity"
                        className="h-7 w-7 rounded-full border border-[#EDE7DC] bg-white flex items-center justify-center text-[#2C2A26] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Minus className="h-[12px] w-[12px]" strokeWidth={2} />
                      </button>
                      <span className="text-[14px] font-medium text-[#2C2A26] min-w-[20px] text-center tabular-nums">
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateItemQty(index, item.quantity + 1)}
                        aria-label="Increase quantity"
                        className="h-7 w-7 rounded-full bg-[#6B9E78] text-white flex items-center justify-center"
                      >
                        <Plus className="h-[12px] w-[12px]" strokeWidth={2} />
                      </button>
                    </div>
                  </div>

                  {item.tracks_expiry && item.confirmed && (
                    <div className="mt-2 flex items-center gap-2 px-1">
                      <input
                        type="date"
                        value={item.expiry_date ?? ''}
                        onChange={e => updateItem(index, { expiry_date: e.target.value || null })}
                        min={new Date().toISOString().slice(0, 10)}
                        className="rounded-[8px] border border-[#EDE7DC] bg-[#FAF8F4] px-2 py-1 text-[12px] text-[#2C2A26] focus:outline-none focus:border-[#6B9E78]"
                      />
                      <span className="text-[10px] italic text-[#9A9491]">
                        Expires (optional)
                      </span>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="text-[10px] uppercase tracking-[0.1em] font-medium text-[#9A9491] px-1 self-start mt-1"
                  aria-label={`Remove ${item.name}`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={handleConfirmAll}
            disabled={editableItems.filter(i => i.confirmed).length === 0}
            className="w-full py-4 rounded-2xl text-base font-semibold text-white disabled:opacity-50"
            style={{ background: '#6B9E78' }}
          >
            Confirm {editableItems.filter(i => i.confirmed).length} item
            {editableItems.filter(i => i.confirmed).length === 1 ? '' : 's'} →
          </button>
        </div>
      ) : null}
    </div>
  )
}
